"use client"

import { useEffect, Suspense } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { verifySession } from '@/features/auth/actions/auth.action'
import { Session } from '@/shared/types/session.type'
import { getPostLoginRedirectRoute } from '@/shared/utils/auth-redirect'
import successToast from '@/shared/lib/toast/success.toast'
import { updateProfileWithGoogleSignupInfo } from '@/features/profile/actions/profile.action'
import { BASIC_ROUTE } from '@/shared/constants/pages-routes'

function AuthCallbackContent() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const stateParam = searchParams.get('state') // Get state from URL if present

  // Parse state parameter to restore URL params (if coming from unified auth)
  useEffect(() => {
    if (stateParam) {
      try {
        // State format: "login|region=2&role=homeowner&team=BlueTeam" or "signup|region=2&role=homeowner"
        const parts = stateParam.split('|')
        if (parts.length > 1) {
          const params = parts[1]
          const urlParams = new URLSearchParams(params)
          
          const region = urlParams.get('region')
          const role = urlParams.get('role')
          const team = urlParams.get('team')
          
          // Store in sessionStorage for use during registration
          if (region) sessionStorage.setItem('unifiedAuthRegion', region)
          if (role) sessionStorage.setItem('unifiedAuthRole', role)
          if (team) sessionStorage.setItem('unifiedAuthTeam', team)
          
          console.log('[AuthCallback] Restored URL params from state:', { region, role, team })
        }
      } catch (error) {
        console.error('[AuthCallback] Failed to parse state parameter:', error)
      }
    }
  }, [stateParam])

  useEffect(() => {
    // Check for error from backend (e.g., email not registered)
    const error = searchParams.get("error")
    const errorMessage = searchParams.get("message")
    
    if (error === 'email_not_registered' || errorMessage) {
      // Get the email from the error message or try to extract it
      // Store the selected Google account email so we can use login_hint on the next attempt
      // This prevents showing the account selection page again
      const emailFromError = searchParams.get("email")
      if (emailFromError) {
        sessionStorage.setItem('selectedGoogleAccountEmail', emailFromError)
        localStorage.setItem('selectedGoogleAccountEmail', emailFromError) // Backup
        console.log('[AuthCallback] Stored selected Google account email for login_hint:', emailFromError)
      }
      
      // Clear any auth cookies
      document.cookie = `aim-auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
      
      // Clear login page flags
      sessionStorage.removeItem('googleLoginFromLoginPage')
      localStorage.removeItem('googleLoginFromLoginPage')
      
      // Redirect to home page with error message
      const params = new URLSearchParams({
        message: errorMessage || 'Email is not registered. Please select one of the following types of accounts',
      })
      window.location.href = `${BASIC_ROUTE.home}?${params.toString()}`
      return
    }
    
    const token = searchParams.get("token")
    if (token) {
      document.cookie = `aim-auth=${token}; path=/; max-age=${7 * 24 * 60 * 60}`

      const decodedSession = (() => {
        try {
          const parts = token.split(".")
          if (parts.length !== 3) return null
          return JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))) as Session
        } catch {
          return null
        }
      })()

      const reloaded = localStorage.getItem("reloadedAfterLogin")

      // Helper function to update profile with Google signup info
      const updateProfileWithSignupInfo = async (session: any): Promise<boolean> => {
        // Check both sessionStorage and localStorage (backup)
        let storedInfo = sessionStorage.getItem('googleSignupInfo')
        if (!storedInfo) {
          storedInfo = localStorage.getItem('googleSignupInfo')
          console.log('[AuthCallback] Found signup info in localStorage (backup)')
        }
        
        if (!storedInfo || !session?.sub) {
          console.warn('[AuthCallback] No signup info or session ID available for profile update', {
            hasStoredInfo: !!storedInfo,
            hasSessionId: !!session?.sub,
          })
          return false
        }

        try {
          const signupInfo = JSON.parse(storedInfo)
          
          // Validate and transform phone to E.164 format before sending
          const { transformToE164, isValidPhoneNumber } = await import('@/shared/lib/utils/phone.utils')
          let phoneE164: string | undefined = undefined
          
          if (signupInfo.phone && signupInfo.phone.trim()) {
            if (isValidPhoneNumber(signupInfo.phone)) {
              phoneE164 = transformToE164(signupInfo.phone)
            } else {
              console.warn('[AuthCallback] Invalid phone number format, skipping phone update:', signupInfo.phone)
            }
          }
          
          // Build update data - only include fields that have values
          const updateData: { fullname?: string; phone?: string } = {}
          
          if (signupInfo.fullname && signupInfo.fullname.trim()) {
            updateData.fullname = signupInfo.fullname.trim()
          }
          
          if (phoneE164) {
            updateData.phone = phoneE164
          }
          
          // Don't proceed if we have nothing to update
          if (!updateData.fullname && !updateData.phone) {
            console.warn('[AuthCallback] No valid data to update in profile')
            sessionStorage.removeItem('googleSignupInfo')
            localStorage.removeItem('googleSignupInfo')
            return false
          }
          
          console.log('[AuthCallback] Updating profile with Google signup info:', {
            userId: session.sub,
            updateData,
          })
          
          // Use server action to update profile (this will also invalidate cache)
          const result = await updateProfileWithGoogleSignupInfo(session.sub, updateData)

          if (result.success) {
            console.log('[AuthCallback] Profile updated successfully and cache invalidated')
            // Clear the stored info after successful update (from both storage locations)
            sessionStorage.removeItem('googleSignupInfo')
            localStorage.removeItem('googleSignupInfo')
            return true
          } else {
            console.error('[AuthCallback] Failed to update user profile:', {
              message: result.message,
              requestData: updateData,
            })
            return false
          }
        } catch (error) {
          console.error('[AuthCallback] Failed to update user info:', error)
          return false
        }
      }

      if (!reloaded) {
        localStorage.setItem("reloadedAfterLogin", "true")

        Promise.resolve(decodedSession)
          .then(async (session) => {
            console.log('[AuthCallback] OAuth callback - checking for signup info', {
              sessionId: session?.sub,
              sessionRole: session?.role,
              sessionEmail: session?.email,
            })
            
            // Check if user came from login page (not signup)
            let fromLoginPage = false
            let loginPageFlag = sessionStorage.getItem('googleLoginFromLoginPage')
            if (!loginPageFlag) {
              loginPageFlag = localStorage.getItem('googleLoginFromLoginPage')
            }
            fromLoginPage = loginPageFlag === 'true'
            
            // Do not block login for currentStep=1 + no phone: invited volunteers (admin-created) match
            // that profile and must still complete /profile. A prior version cleared the session here and
            // showed "Email is not registered" — incorrect whenever the backend already issued a token.
            
            // Clear login page flag after checking
            if (fromLoginPage) {
              sessionStorage.removeItem('googleLoginFromLoginPage')
              localStorage.removeItem('googleLoginFromLoginPage')
            }
            
            // Check if we have stored Google signup info (new registration)
            // Check both sessionStorage and localStorage (backup)
            // Only treat as new registration if it has the fromSignupPage flag
            let storedInfo = sessionStorage.getItem('googleSignupInfo')
            if (!storedInfo) {
              storedInfo = localStorage.getItem('googleSignupInfo')
              console.log('[AuthCallback] Found signup info in localStorage (backup)')
            }
            console.log('[AuthCallback] Stored signup info:', storedInfo)
            
            let isNewRegistration = false
            if (storedInfo) {
              try {
                const signupInfo = JSON.parse(storedInfo)
                console.log('[AuthCallback] Parsed signup info:', signupInfo)
                isNewRegistration = signupInfo.fromSignupPage === true
                console.log('[AuthCallback] Is new registration?', isNewRegistration)
              } catch (e) {
                console.error('[AuthCallback] Failed to parse stored info:', e)
                isNewRegistration = false
              }
            }
            
            // Update profile with signup info and wait for it to complete
            if (isNewRegistration && session?.sub) {
              console.log('[AuthCallback] Starting profile update for new registration')
              const updateSuccess = await updateProfileWithSignupInfo(session)
              console.log('[AuthCallback] Profile update result:', updateSuccess)
              
              if (!updateSuccess) {
                console.error('[AuthCallback] Profile update failed, but continuing with redirect')
              }
              
              // Clear stored Google account email after successful signup
              sessionStorage.removeItem('selectedGoogleAccountEmail')
              localStorage.removeItem('selectedGoogleAccountEmail')
            } else {
              console.log('[AuthCallback] Skipping profile update - not a new registration or missing session ID', {
                isNewRegistration,
                hasSessionId: !!session?.sub,
              })
            }

            // Get profile to check currentStep for redirect logic
            let currentStep: number | undefined
            if (session?.sub && !isNewRegistration) {
              try {
                const { getProfile } = await import('@/features/profile/actions/profile.action')
                const profileRes = await getProfile()
                if (profileRes.success && profileRes.data) {
                  currentStep = profileRes.data.currentStep
                }
              } catch (error) {
                console.error('[AuthCallback] Error fetching profile for redirect (reload):', error)
              }
            }
            
            // Redirect to Profile for new registrations, otherwise use normal login redirect
            const redirectRoute = isNewRegistration 
              ? "/profile"
              : (session?.role ? getPostLoginRedirectRoute(session.role, null, currentStep) : "/home")

            // Add a small delay to ensure cache invalidation completes before redirect
            setTimeout(() => {
              window.location.href = redirectRoute
              successToast("Google login successful")
            }, 100)
          })
          .catch(() => {
            window.location.href = "/home"
          })
      } else {
        // 🔑 Always clear so next login works
        localStorage.removeItem("reloadedAfterLogin")

        Promise.resolve(decodedSession)
          .then(async (session) => {
            console.log('[AuthCallback] OAuth callback (reload) - checking for signup info', {
              sessionId: session?.sub,
              sessionRole: session?.role,
              sessionEmail: session?.email,
            })
            
            // Check if user came from login page (not signup)
            let fromLoginPage = false
            let loginPageFlag = sessionStorage.getItem('googleLoginFromLoginPage')
            if (!loginPageFlag) {
              loginPageFlag = localStorage.getItem('googleLoginFromLoginPage')
            }
            fromLoginPage = loginPageFlag === 'true'
            
            // Same as non-reload path: do not block invited volunteers (currentStep=1, no phone).
            
            // Clear login page flag after checking
            if (fromLoginPage) {
              sessionStorage.removeItem('googleLoginFromLoginPage')
              localStorage.removeItem('googleLoginFromLoginPage')
            }
            
            // Check if we have stored Google signup info (new registration)
            // Check both sessionStorage and localStorage (backup)
            // Only treat as new registration if it has the fromSignupPage flag
            let storedInfo = sessionStorage.getItem('googleSignupInfo')
            if (!storedInfo) {
              storedInfo = localStorage.getItem('googleSignupInfo')
              console.log('[AuthCallback] Found signup info in localStorage (backup reload)')
            }
            console.log('[AuthCallback] Stored signup info (reload):', storedInfo)
            
            let isNewRegistration = false
            if (storedInfo) {
              try {
                const signupInfo = JSON.parse(storedInfo)
                console.log('[AuthCallback] Parsed signup info (reload):', signupInfo)
                isNewRegistration = signupInfo.fromSignupPage === true
                console.log('[AuthCallback] Is new registration? (reload)', isNewRegistration)
              } catch (e) {
                console.error('[AuthCallback] Failed to parse stored info (reload):', e)
                isNewRegistration = false
              }
            }
            
            // Update profile with signup info and wait for it to complete
            if (isNewRegistration && session?.sub) {
              console.log('[AuthCallback] Starting profile update for new registration (reload)')
              const updateSuccess = await updateProfileWithSignupInfo(session)
              console.log('[AuthCallback] Profile update result (reload):', updateSuccess)
              
              if (!updateSuccess) {
                console.error('[AuthCallback] Profile update failed (reload), but continuing with redirect')
              }
              
              // Clear stored Google account email after successful signup
              sessionStorage.removeItem('selectedGoogleAccountEmail')
              localStorage.removeItem('selectedGoogleAccountEmail')
            } else {
              console.log('[AuthCallback] Skipping profile update (reload) - not a new registration or missing session ID', {
                isNewRegistration,
                hasSessionId: !!session?.sub,
              })
            }
            
            // Get profile to check currentStep for redirect logic
            let currentStep: number | undefined
            if (session?.sub && !isNewRegistration) {
              try {
                const { getProfile } = await import('@/features/profile/actions/profile.action')
                const profileRes = await getProfile()
                if (profileRes.success && profileRes.data) {
                  currentStep = profileRes.data.currentStep
                }
              } catch (error) {
                console.error('[AuthCallback] Error fetching profile for redirect (reload):', error)
              }
            }
            
            // Redirect to Profile for new registrations, otherwise use normal login redirect
            const redirectRoute = isNewRegistration 
              ? "/profile"
              : (session?.role ? getPostLoginRedirectRoute(session.role, null, currentStep) : "/home")

            // Add a small delay to ensure cache invalidation completes before redirect
            setTimeout(() => {
              window.location.href = redirectRoute
              successToast("Google login successful")
            }, 100)
          })
          .catch(() => {
            window.location.href = "/home"
          })
      }
    }
  }, [searchParams, router])

  // Show a simple loading state while processing
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  )
}

function AuthCallbackPage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    }>
      <AuthCallbackContent />
    </Suspense>
  )
}

export default AuthCallbackPage

